/**
 * Eval Metrics React Query Hooks
 * Data fetching and mutations for evaluation metric definitions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { evalMetricsApi } from '@/lib/api/evalMetrics';
import type {
  CreateMetricDefinitionRequest,
  UpdateMetricDefinitionRequest,
  MetricGenerateRequest,
} from '@/types/evalScenarios';

/**
 * Hook to fetch all metric definitions with optional filters
 */
export function useEvalMetrics(params?: {
  metric_type?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['eval-metrics', params],
    queryFn: () => evalMetricsApi.list(params),
  });
}

/**
 * Hook to fetch a single metric definition by name
 */
export function useEvalMetric(name: string | null) {
  return useQuery({
    queryKey: ['eval-metric', name],
    queryFn: () => evalMetricsApi.get(name!),
    enabled: !!name,
  });
}

/**
 * Hook to fetch the full prompt for a metric
 */
export function useMetricPrompt(name: string | null) {
  return useQuery({
    queryKey: ['eval-metric-prompt', name],
    queryFn: () => evalMetricsApi.getPrompt(name!),
    enabled: !!name,
  });
}

/**
 * Hook to fetch metrics for a specific agent type
 */
export function useMetricsForAgent(agentType: string | null) {
  return useQuery({
    queryKey: ['eval-metrics-for-agent', agentType],
    queryFn: () => evalMetricsApi.getForAgent(agentType!),
    enabled: !!agentType,
  });
}

/**
 * Hook to create a new metric definition
 */
export function useCreateMetricDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMetricDefinitionRequest) => evalMetricsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-metrics'] });
    },
  });
}

/**
 * Hook to update a metric definition
 */
export function useUpdateMetricDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: UpdateMetricDefinitionRequest }) =>
      evalMetricsApi.update(name, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eval-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['eval-metric', variables.name] });
      queryClient.invalidateQueries({ queryKey: ['eval-metric-prompt', variables.name] });
    },
  });
}

/**
 * Hook to delete a metric definition
 */
export function useDeleteMetricDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => evalMetricsApi.delete(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-metrics'] });
    },
  });
}

/**
 * Hook to generate metric criteria and steps using AI
 */
export function useGenerateMetric() {
  return useMutation({
    mutationFn: (data: MetricGenerateRequest) => evalMetricsApi.generate(data),
  });
}
